import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF, useTexture, Environment, useCursor } from "@react-three/drei";
import { useSnapshot } from "valtio";
import { state, meshParts, infopoints } from "./store";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import CameraControls from "camera-controls";

const model = process.env.PUBLIC_URL + "/assets/ALAM_UVMirror.glb";
CameraControls.install({ THREE });
const color = new THREE.Color()

const Controls = ({
  pos = new THREE.Vector3(),
  look = new THREE.Vector3(),
}) => {
  const snap = useSnapshot(state);
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const controls = useMemo(
    () => new CameraControls(camera, gl.domElement),
    [camera, gl.domElement]
  );
  return useFrame((state, delta) => {
    snap.focusDetail
      ? pos.set(snap.infopoint.cameraPosition[0], snap.infopoint.cameraPosition[1], snap.infopoint.cameraPosition[2])
      : pos.set(0, 0, 1.5);
    snap.focusDetail ? look.set(snap.infopoint.position[0], snap.infopoint.position[1], snap.infopoint.position[2]) : look.set(0, 0, 0);

    state.camera.position.lerp(pos, 0.5);
    state.camera.updateProjectionMatrix();

    controls.setLookAt(
      state.camera.position.x,
      state.camera.position.y,
      state.camera.position.z,
      look.x,
      look.y,
      look.z,
      true
    );
    return controls.update(delta);
  });
};

const Cloud = ({zoomToView}) => {
  const snap = useSnapshot(state);
  const texture = useTexture(
    process.env.PUBLIC_URL + "/assets/UI_Info_Icon.png"
  );
  const camera = useThree((state) => state.camera);
  const [hovered, setHovered] = useState();
  const groupRef = useRef();

  const size = [0.05, 0.05];

  const refInfo01 = useRef();
  const refInfo02 = useRef();
  const refInfo03 = useRef();
  const refInfo04 = useRef();
  const refInfo05 = useRef();

  useFrame((state) => {
    // nullcheck
    if (!refInfo01.current) return;
    // rotazione icone verso la camera
    refInfo01.current.lookAt(camera.position);
    refInfo02.current.lookAt(camera.position);
    refInfo03.current.lookAt(camera.position);
    refInfo04.current.lookAt(camera.position);
    refInfo05.current.lookAt(camera.position);

    // cambio colore con l'hover
    groupRef.current.children.forEach((child, index) => {
      child.material.color.lerp(color.set(hovered === child.id ? snap.color : "white"), hovered ? 0.1 : 0.05)
    })
  });

  const handleClick = (index) => {
    state.infopoint = infopoints[index];
    state.focusDetail = true;
    setHovered(false);
    zoomToView();
  };

  useEffect(() => {
    // if (hovered)
    //   groupRef.current.getObjectById(hovered).material.color.set("black");
    document.body.style.cursor = hovered ? "pointer" : "auto";
  }, [hovered]);

  if (snap.focusDetail) {
    return <></>;
  }

  return (
    <>
      <group
        ref={groupRef}
        onPointerOver={(e) => setHovered(e.object.id)}
        onPointerLeave={() => setHovered(undefined)}
      >
        <mesh
          position={infopoints[0].position}
          dispose={null}
          onClick={() => handleClick(0)}
          ref={refInfo01}
        >
          <planeGeometry attach="geometry" args={size} />
          <meshBasicMaterial
            attach="material"
            side={THREE.DoubleSide}
            map={texture}
            transparent
          />
        </mesh>
        <mesh
          position={infopoints[1].position}
          dispose={null}
          onClick={() => handleClick(1)}
          ref={refInfo02}
        >
          <planeGeometry attach="geometry" args={size} />
          <meshBasicMaterial
            attach="material"
            side={THREE.DoubleSide}
            map={texture}
            transparent
          />
        </mesh>
        <mesh
          position={infopoints[2].position}
          dispose={null}
          onClick={() => handleClick(2)}
          ref={refInfo03}
        >
          <planeGeometry attach="geometry" args={size} />
          <meshBasicMaterial
            attach="material"
            side={THREE.DoubleSide}
            map={texture}
            transparent
          />
        </mesh>
        <mesh
          position={infopoints[3].position}
          dispose={null}
          onClick={() => handleClick(3)}
          ref={refInfo04}
        >
          <planeGeometry attach="geometry" args={size} />
          <meshBasicMaterial
            attach="material"
            side={THREE.DoubleSide}
            map={texture}
            transparent
          />
        </mesh>
        <mesh
          position={infopoints[4].position}
          dispose={null}
          onClick={() => handleClick(4)}
          ref={refInfo05}
        >
          <planeGeometry attach="geometry" args={size} />
          <meshBasicMaterial
            attach="material"
            side={THREE.DoubleSide}
            map={texture}
            transparent
          />
        </mesh>
      </group>
    </>
  );
};

export const Canvas2 = ({ showPOI }) => {
  const snap = useSnapshot(state);
  const [zoomDelay, setZoomDelay] = useState(false);
  const [active, setActive] = useState(false);

  useCursor(active);

  const handleClick = useCallback(async () => {
    if (snap.focusDetail) {
      state.beforeExitFocus = true;
      state.focusDetail = false;
      await new Promise((r) => setTimeout(r, 1000));
      state.beforeExitFocus = false;
      setZoomDelay(false);
    }
  }, [snap]);

  useEffect(() => {
    document.addEventListener("pointerdown", handleClick);

    return () => {
      document.removeEventListener("pointerdown", handleClick);
    };
  }, [handleClick]);

  const handleZoom = (e) => {
    setZoomDelay(true);
  }

  return (
    <>
      <Canvas
        shadows
        gl={{ preserveDrawingBuffer: true }}
        eventSource={document.getElementById("root")}
        eventPrefix="client"
      >
        {zoomDelay ? (
          <Controls />
        ) : (
          <OrbitControls
            // minAzimuthAngle={-Math.PI / 4}
            // maxAzimuthAngle={Math.PI / 4}
            // minPolarAngle={Math.PI / 6}
            // maxPolarAngle={Math.PI - Math.PI / 6}
            maxDistance={1.4}
          />
        )}
        <ambientLight intensity={0.2} />
        <Environment files={'/assets/venice_sunset_1k.hdr'}/>
        <AlaM position={[0, 0, 0]} rotation-x={0.4} rotation-y={0.4} />
        {showPOI && (
          <Cloud
            position={[0, 0, 0]}
            rotation-x={0.4}
            rotation-y={0.4}
            zoomToView={handleZoom}
            setActive={setActive}
          />
        )}
      </Canvas>
    </>
  );
};

const AlaM = (props) => {
  const snap = useSnapshot(state);
  const texture = {};
  let c = 0;
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  texture[meshParts[c]] = useTexture(
    `${process.env.PUBLIC_URL}/assets/TextureAlaM/${snap.texture[meshParts[c++]]
    }`
  );
  // texture[meshParts[c]] = useTexture(`/assets/TextureAlaM/${snap.texture[meshParts[c++]]}`);

  const { nodes } = useGLTF(model);

  return (
    <>
      <mesh
        castShadow
        geometry={nodes.Sopra.geometry}
        {...props}
        dispose={null}
      >
        <meshStandardMaterial
          map={texture["top"]}
          roughnessMap={
            snap.texture.topRoughness !== "Normal_Base.jpg"
              ? texture["topRoughness"]
              : null
          }
          metalnessMap={
            snap.texture.topMetallic !== "Normal_Base.jpg"
              ? texture["topMetallic"]
              : null
          }
          normalMap={
            snap.texture.topNormal !== "Normal_Base.jpg"
              ? texture["topNormal"]
              : null
          }
        ></meshStandardMaterial>
      </mesh>
      <mesh
        castShadow
        geometry={nodes.Sotto.geometry}
        {...props}
        dispose={null}
      >
        <meshStandardMaterial
          map={texture["bot"]}
          roughnessMap={
            snap.texture.botRoughness !== "Normal_Base.jpg"
              ? texture["botRoughness"]
              : null
          }
          metalnessMap={
            snap.texture.botMetallic !== "Normal_Base.jpg"
              ? texture["botMetallic"]
              : null
          }
          normalMap={
            snap.texture.botNormal !== "Normal_Base.jpg"
              ? texture["botNormal"]
              : null
          }
        ></meshStandardMaterial>
      </mesh>
      <mesh
        castShadow
        geometry={nodes.Other.geometry}
        {...props}
        dispose={null}
      >
        <meshStandardMaterial
          map={texture["other"]}
          roughnessMap={
            snap.texture.otherRoughness !== "Normal_Base.jpg"
              ? texture["topRoughness"]
              : null
          }
          metalnessMap={
            snap.texture.otherMetallic !== "Normal_Base.jpg"
              ? texture["otherMetallic"]
              : null
          }
          normalMap={
            snap.texture.otherNormal !== "Normal_Base.jpg"
              ? texture["otherNormal"]
              : null
          }
        ></meshStandardMaterial>
      </mesh>
    </>
  );
};

useGLTF.preload(model);
// materialsAlaM.map().forEach(useTexture.preload);
